import { apiClient, handleApiResponse } from './client';
import type { ApiResponse } from '@/types';

/**
 * Custom Events API Service
 * Handles CRUD operations for custom event definitions
 */

export interface CustomEventDefinition {
  event_id: string;
  store_id: string;
  event_name: string;
  display_name: string;
  description?: string;
  category: 'interaction' | 'conversion_signal' | 'engagement' | 'navigation';
  tracking_config: TrackingConfig;
  property_schema?: Record<string, unknown>;
  ga4_config?: GA4Config | null;
  status: 'active' | 'draft' | 'archived'; // Phase 4: Added 'draft' status
  tags?: string[];
  experiment_id?: string; // Phase 4: Link to experiment if created inline
  variant_scope?: 'all' | 'control' | 'treatment'; // Phase 4: Variant scope
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface TrackingConfig {
  mode: 'manual' | 'auto';
  auto_config?: AutoTrackingConfig;
}

export interface AutoTrackingConfig {
  interaction_type: 'click' | 'visibility' | 'form_change' | 'scroll' | 'media_view';
  click_config?: ClickConfig;
  visibility_config?: VisibilityConfig;
  form_change_config?: FormChangeConfig;
  scroll_config?: ScrollConfig;
  media_view_config?: MediaViewConfig;
}

export interface ClickConfig {
  selector: string;
  capture_text?: boolean;
  debounce_ms?: number;
}

export interface VisibilityConfig {
  selector: string;
  threshold?: number;
  duration_ms?: number;
}

export interface FormChangeConfig {
  selector: string;
  debounce_ms?: number;
}

export interface ScrollConfig {
  depth_percentage?: number;
  element_selector?: string;
}

/**
 * Tracks when a visitor sees an image. The image is identified either by CSS
 * selector or by a filename glob matched against its src (`*` wildcard).
 */
export interface MediaViewConfig {
  match_by: 'selector' | 'src_pattern';
  /** Required when match_by === 'selector'. */
  selector?: string;
  /** Filename glob (`*` wildcard). Required when match_by === 'src_pattern'. */
  src_pattern?: string;
  /** IntersectionObserver ratio, 0-1. Default 0.5. */
  threshold?: number;
  /** Minimum continuous visible time before firing, ms. Default 0. */
  dwell_ms?: number;
  /** Also consider CSS background-image elements. Selector mode only. */
  include_background?: boolean;
}

export interface GA4Config {
  ga4_event_name: string;
}

export interface GA4ValidationResponse {
  event_id: string;
  message: string;
  exists: boolean;
  event_count_7d: number;
  ga4_event_name: string;
  validation_status: 'valid' | 'event_not_found' | 'ga4_not_configured';
  last_validated: string;
}

export interface CreateCustomEventRequest {
  store_id: string;
  event_name: string;
  display_name: string;
  description?: string;
  category: CustomEventDefinition['category'];
  tracking_config: TrackingConfig;
  property_schema?: Record<string, unknown>;
  ga4_config?: GA4Config | null;
  tags?: string[];
  /** Link the event to an experiment (e.g. events created inline during experiment creation). */
  experiment_id?: string;
  /** Variant scope for experiment-linked events. Defaults to 'all' server-side. */
  variant_scope?: 'all' | 'control' | 'treatment';
}

export interface UpdateCustomEventRequest {
  display_name?: string;
  description?: string;
  tracking_config?: TrackingConfig;
  property_schema?: Record<string, unknown>;
  ga4_config?: GA4Config | null;
  tags?: string[];
  status?: 'active' | 'draft' | 'archived'; // Phase 4: Allow status updates
}

export interface ListCustomEventsResponse {
  custom_events: CustomEventDefinition[];
  count: number;
}

export interface CustomEventMetrics {
  event_id: string;
  event_name: string;
  display_name: string;
  category: string;
  total_occurrences: number;
  unique_visitors: number;
  unique_sessions: number;
  visitor_engagement_rate: number;
  session_engagement_rate: number;
  top_properties: Record<string, Array<{ value: string; count: number }>>;
  ga4_binding?: {
    ga4_event_name: string;
    validated: boolean;
    last_validated: string | null;
    event_count_7d: number;
  } | null;
}

// Conversion propensity metrics for a single custom event
export interface CustomEventPropensityMetrics {
  event_id: string;
  event_name: string;

  // Session counts
  sessions_with_event: number;
  sessions_without_event: number;
  session_engagement_rate?: number;

  // Conversion counts
  conversions_with_event: number;
  conversions_without_event: number;

  // Conversion rates
  conversion_rate_with_event: number;
  conversion_rate_without_event: number;
  baseline_conversion_rate: number;

  // Lift metrics
  conversion_lift: number;          // Relative lift (e.g., 0.5 = 50% improvement)
  conversion_lift_percentage: number; // Lift as percentage (e.g., 50)

  // Statistical significance
  z_score: number;
  p_value: number;
  is_significant: boolean;
  confidence_level: number;
  direction: 'positive' | 'negative' | 'neutral';

  // Sample size adequacy
  sample_size_adequate: boolean;
}

// Aggregated propensity data for all custom events
export interface CustomEventsPropensity {
  baseline_conversion_rate: number;
  total_sessions: number;
  total_conversions: number;
  events: Record<string, CustomEventPropensityMetrics>;
}

export interface CustomEventsSummary {
  total_events: number;
  unique_visitors_with_events: number;
  total_event_definitions: number;
}

/**
 * Create a new custom event definition
 */
export async function createCustomEvent(
  data: CreateCustomEventRequest
): Promise<{ message: string; event: CustomEventDefinition }> {
  return apiClient.post('/api/custom-events/definitions', data);
}

/**
 * List custom event definitions for a store
 */
export async function listCustomEvents(
  storeId: string,
  status?: 'active' | 'draft' | 'archived' // Phase 4: Added 'draft'
): Promise<ListCustomEventsResponse> {
  const params: Record<string, string> = { store_id: storeId };
  if (status) {
    params.status = status;
  }

  return apiClient.get('/api/custom-events/definitions', params);
}

/**
 * Get a specific custom event definition by ID
 */
export async function getCustomEvent(eventId: string): Promise<CustomEventDefinition> {
  return apiClient.get(`/api/custom-events/definitions/${eventId}`);
}

/**
 * Update a custom event definition
 */
export async function updateCustomEvent(
  eventId: string,
  data: UpdateCustomEventRequest
): Promise<{ message: string; event: CustomEventDefinition }> {
  return apiClient.put(`/api/custom-events/definitions/${eventId}`, data);
}

/**
 * Archive (soft delete) a custom event definition
 */
export async function archiveCustomEvent(
  eventId: string
): Promise<{ message: string; event: CustomEventDefinition }> {
  return apiClient.delete(`/api/custom-events/definitions/${eventId}`);
}

/**
 * Validate GA4 binding for a custom event (Phase 3)
 */
export async function validateGA4Binding(
  eventId: string
): Promise<GA4ValidationResponse> {
  return apiClient.post(
    `/api/custom-events/definitions/${eventId}/validate-ga4-binding`
  );
}

/**
 * Activate a draft custom event (Phase 4)
 */
export async function activateCustomEvent(
  eventId: string
): Promise<{ message: string; event: CustomEventDefinition }> {
  return updateCustomEvent(eventId, { status: 'active' } as UpdateCustomEventRequest);
}

export interface CustomEventsTimelinePoint {
  date: string;
  snapshot_type: string;
  window_start: string;
  window_end: string;
  total_sessions: number;
  events: Record<
    string,
    {
      event_id: string;
      total_occurrences: number;
      unique_sessions: number;
      conversion_lift: number | null;
      conversion_rate_with_event: number | null;
      is_significant: boolean | null;
    }
  >;
}

export interface CustomEventsTimelineResponse {
  store_id: string;
  window_days: number;
  window_start: string;
  window_end: string;
  series: CustomEventsTimelinePoint[];
  events_index: Array<Pick<CustomEventDefinition, 'event_id' | 'event_name' | 'display_name' | 'category'>>;
}

/**
 * Per-day timeline of custom events from daily snapshots
 */
export async function getCustomEventsTimeline(
  storeId: string,
  windowDays = 7
): Promise<CustomEventsTimelineResponse> {
  const res = await apiClient.get<ApiResponse<CustomEventsTimelineResponse>>(
    '/api/store/funnel/analytics',
    { store_id: storeId, window: windowDays, timeline: 'true' }
  );
  return handleApiResponse(res);
}

/**
 * Custom Events Service
 */
export const customEventsService = {
  create: createCustomEvent,
  list: listCustomEvents,
  get: getCustomEvent,
  update: updateCustomEvent,
  archive: archiveCustomEvent,
  validateGA4: validateGA4Binding,
  activate: activateCustomEvent, // Phase 4: Activate draft events
  getTimeline: getCustomEventsTimeline,
};

export default customEventsService;
